import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";

const buttonClass =
  "mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-tide px-3 py-3 text-[14.5px] font-medium text-paper-hi transition hover:-translate-y-px hover:bg-tide-d hover:shadow-[0_10px_30px_-8px_rgba(38,99,199,0.35)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:bg-tide disabled:hover:shadow-none";

/** User-facing messages keyed on machine-readable API `code`. */
const ERROR_MESSAGES: Record<string, string> = {
  invalid_token: "This link is invalid.",
  expired_token:
    "This link has expired. Request a new email change from your account settings.",
  used_token: "This link has already been used.",
  email_conflict: "This email address is no longer available.",
  account_disabled: "Your account has been disabled.",
  rate_limited: "Too many attempts. Please try again in a few minutes.",
};

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative min-h-screen overflow-hidden bg-paper text-ink antialiased pt-[var(--banner-h)]">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-[140px] -top-[200px] h-[520px] w-[520px] rounded-full bg-tide/10 blur-[80px]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-[240px] -right-[120px] h-[460px] w-[460px] rounded-full bg-spark/10 blur-[80px]"
      />

      <nav className="relative z-20 flex items-center justify-between px-8 pt-7 md:px-14">
        <div className="flex items-center gap-2.5">
          <img
            src="/reflex-mark.svg"
            alt=""
            width="32"
            height="32"
            className="h-8 w-8"
          />
          <span className="text-[15px] font-semibold tracking-tight">
            Reflex Labs
          </span>
        </div>
        <a
          href="mailto:hello@reflexlabs.io"
          className="text-[13px] text-ink-4 transition hover:text-ink"
        >
          Need help?
        </a>
      </nav>

      <main className="relative z-10 flex min-h-[calc(100vh-140px)] items-center justify-center px-6 py-12">
        <div className="portal-reveal portal-reveal-1 w-full max-w-[400px]">
          {children}
        </div>
      </main>

      <footer className="relative z-10 flex items-center justify-between px-8 py-6 text-[11.5px] text-ink-4 md:px-14">
        <span>&copy; {new Date().getFullYear()} Reflex Labs</span>
        <Link to="/login" className="hover:text-ink-3">
          Back to sign in
        </Link>
      </footer>
    </div>
  );
}

export function ConfirmEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [status, setStatus] = useState<"loading" | "success" | "error">(
    token ? "loading" : "error",
  );
  const [errorMessage, setErrorMessage] = useState<string>(
    token ? "" : "This link is invalid.",
  );

  // Guard against StrictMode double-fire.
  const submitted = useRef(false);

  useEffect(() => {
    if (!token || submitted.current) return;
    submitted.current = true;

    (async () => {
      try {
        const response = await fetch("/auth/email/change-confirm", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const data = (await response.json().catch(() => ({}))) as {
          ok?: boolean;
          error?: string;
          code?: string;
        };
        if (!response.ok || !data.ok) {
          const code = data.code ?? "";
          setErrorMessage(
            ERROR_MESSAGES[code] ||
              data.error ||
              "Something went wrong. Please try again.",
          );
          setStatus("error");
          return;
        }
        setStatus("success");
      } catch {
        setErrorMessage("Could not reach the server. Please try again.");
        setStatus("error");
      }
    })();
  }, [token]);

  if (status === "loading") {
    return (
      <Shell>
        <div className="mb-7 text-center">
          <h1 className="font-display text-[36px] font-medium leading-tight tracking-tight">
            Confirming <em className="font-normal italic text-tide">your email</em>&hellip;
          </h1>
        </div>
        <div className="login-card-shadow rounded-2xl bg-paper-hi p-8 text-center">
          <p className="text-[14.5px] leading-relaxed text-ink-3">
            Please wait while we verify your email change.
          </p>
        </div>
      </Shell>
    );
  }

  if (status === "success") {
    return (
      <Shell>
        <div className="mb-7 text-center">
          <h1 className="font-display text-[36px] font-medium leading-tight tracking-tight">
            Email <em className="font-normal italic text-tide">updated</em>.
          </h1>
        </div>
        <div className="login-card-shadow rounded-2xl bg-paper-hi p-8 text-center">
          <p className="text-[14.5px] leading-relaxed text-ink-3">
            Your email address has been updated. Sign in with your new address.
          </p>
          <Link to="/login" className={buttonClass + " mt-6"}>
            Go to sign in
          </Link>
        </div>
      </Shell>
    );
  }

  // status === "error"
  return (
    <Shell>
      <div className="mb-7 text-center">
        <h1 className="font-display text-[36px] font-medium leading-tight tracking-tight">
          Something went{" "}
          <em className="font-normal italic text-rust">wrong</em>.
        </h1>
      </div>
      <div className="login-card-shadow rounded-2xl bg-paper-hi p-8 text-center">
        <div
          role="alert"
          className="rounded-lg border border-rust/30 bg-rust-s/40 px-3 py-2 text-[13px] text-rust"
        >
          {errorMessage}
        </div>
        <div className="mt-5 text-[13px] text-ink-4">
          <Link to="/login" className="font-medium text-tide hover:underline">
            Back to sign in
          </Link>
        </div>
      </div>
    </Shell>
  );
}
